import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  HeartStraight,
  CheckCircle,
  ShieldCheck,
  ArrowUpRight,
} from "@phosphor-icons/react";
import { API_BASE } from "../lib/site";

const inputCls =
  "w-full rounded-xl border border-ink-200 px-4 py-2.5 bg-white text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all";

const FieldLabel = ({ children, required }) => (
  <label className="block text-sm font-semibold text-ink-800 mb-1.5">
    {children}
    {required && <span className="text-red-500 ml-0.5">*</span>}
  </label>
);

const defaultBookplate = (giftType, honoree) => {
  const name = (honoree || "").trim() || "___________";
  return giftType === "in_honor"
    ? `Given in honor of ${name}`
    : `Given in loving memory of ${name}`;
};

const MemorialHonorForm = ({ onComplete }) => {
  const [form, setForm] = useState({
    gift_type: "in_memory",
    honoree_name: "",
    bookplate_text: defaultBookplate("in_memory", ""),
    bookplate_edited: false,
    donor_name: "",
    donor_email: "",
    notify_recipient: false,
    recipient_name: "",
    recipient_email: "",
    recipient_mailing_address: "",
    note: "",
    preferred_placement: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  // Auto-prefill bookplate until the donor edits it manually.
  useEffect(() => {
    if (form.bookplate_edited) return;
    setForm((f) => ({
      ...f,
      bookplate_text: defaultBookplate(f.gift_type, f.honoree_name),
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.gift_type, form.honoree_name]);

  const set = (k) => (e) => {
    const v = e?.target
      ? e.target.type === "checkbox"
        ? e.target.checked
        : e.target.value
      : e;
    setForm((f) => ({ ...f, [k]: v }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (
      !form.honoree_name ||
      !form.bookplate_text ||
      !form.donor_name ||
      !form.donor_email
    ) {
      toast.error("Please fill in the required fields.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API_BASE}/sponsor-intents`, {
        gift_type: form.gift_type,
        honoree_name: form.honoree_name.trim(),
        bookplate_text: form.bookplate_text.trim(),
        donor_name: form.donor_name.trim(),
        donor_email: form.donor_email.trim(),
        notify_recipient: form.notify_recipient,
        recipient_name: form.notify_recipient ? form.recipient_name : null,
        recipient_email: form.notify_recipient && form.recipient_email
          ? form.recipient_email : null,
        recipient_mailing_address: form.notify_recipient
          ? form.recipient_mailing_address : null,
        note: form.note || null,
        preferred_placement: form.preferred_placement || null,
        consent: true,
        source_page: typeof window !== "undefined" ? window.location.href : null,
      });
      setSubmitted(true);
      toast.success("Memorial/honor details saved.");
      // Give the donor a beat to read the confirmation, then continue to /donate.
      setTimeout(() => onComplete?.(), 1800);
    } catch (err) {
      const msg =
        err?.response?.data?.detail?.[0]?.msg ||
        err?.response?.data?.detail ||
        "We couldn't save your details. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Please check the form fields.");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div
        data-testid="sab-memorial-form-success"
        className="rounded-2xl border border-emerald-200 bg-emerald-50/70 p-6 sm:p-8 text-center"
      >
        <span className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-emerald-500 text-white">
          <CheckCircle size={24} weight="fill" />
        </span>
        <h3 className="mt-4 font-heading text-xl sm:text-2xl text-ink-900 font-medium">
          Details saved. Continuing to the giving page…
        </h3>
        <p className="mt-3 text-ink-700 text-sm max-w-md mx-auto leading-relaxed">
          BALM will use these details to prepare the bookplate and any
          acknowledgment after your gift is received.
        </p>
      </div>
    );
  }

  return (
    <form
      onSubmit={onSubmit}
      data-testid="sab-memorial-form"
      className="bg-white rounded-2xl border border-ink-100 shadow-[0_8px_30px_rgba(0,0,0,0.04)] p-6 sm:p-8 space-y-6"
      noValidate
    >
      {/* Gift type */}
      <fieldset className="space-y-3">
        <FieldLabel required>Gift Type</FieldLabel>
        <div className="grid sm:grid-cols-2 gap-3">
          {[
            { v: "in_memory", l: "In Memory Of" },
            { v: "in_honor", l: "In Honor Of" },
          ].map((o) => (
            <label
              key={o.v}
              className={`flex items-center gap-3 rounded-xl border px-4 py-3 cursor-pointer transition-colors ${
                form.gift_type === o.v
                  ? "border-primary bg-primary/5"
                  : "border-ink-200 hover:bg-ink-50"
              }`}
            >
              <input
                type="radio"
                name="gift_type"
                value={o.v}
                checked={form.gift_type === o.v}
                onChange={set("gift_type")}
                data-testid={`sab-gift-type-${o.v}`}
                className="accent-primary"
              />
              <span className="font-semibold text-ink-800 text-sm">{o.l}</span>
            </label>
          ))}
        </div>
      </fieldset>

      <div>
        <FieldLabel required>
          Name of person being {form.gift_type === "in_honor" ? "honored" : "remembered"}
        </FieldLabel>
        <input
          type="text"
          required
          value={form.honoree_name}
          onChange={set("honoree_name")}
          data-testid="sab-honoree-name"
          className={inputCls}
        />
      </div>

      <div>
        <FieldLabel required>Bookplate wording</FieldLabel>
        <input
          type="text"
          required
          value={form.bookplate_text}
          onChange={(e) =>
            setForm((f) => ({
              ...f,
              bookplate_text: e.target.value,
              bookplate_edited: true,
            }))
          }
          data-testid="sab-bookplate-text"
          className={inputCls}
        />
        <p className="mt-1.5 text-[11px] text-ink-500">
          Prefilled from your gift type. Edit if you'd like different wording.
        </p>
      </div>

      <div className="grid sm:grid-cols-2 gap-5">
        <div>
          <FieldLabel required>Your name</FieldLabel>
          <input
            type="text"
            required
            value={form.donor_name}
            onChange={set("donor_name")}
            autoComplete="name"
            data-testid="sab-donor-name"
            className={inputCls}
          />
        </div>
        <div>
          <FieldLabel required>Your email</FieldLabel>
          <input
            type="email"
            required
            value={form.donor_email}
            onChange={set("donor_email")}
            autoComplete="email"
            data-testid="sab-donor-email"
            className={inputCls}
          />
        </div>
      </div>

      {/* Notify recipient */}
      <fieldset className="rounded-xl border border-ink-100 bg-paper/50 p-4 space-y-4">
        <div>
          <FieldLabel>
            Would you like BALM to notify someone about this gift?
          </FieldLabel>
          <div className="flex gap-6 text-sm mt-2">
            {[
              { v: false, l: "No" },
              { v: true, l: "Yes" },
            ].map((o) => (
              <label key={String(o.v)} className="inline-flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="notify_recipient"
                  checked={form.notify_recipient === o.v}
                  onChange={() =>
                    setForm((f) => ({ ...f, notify_recipient: o.v }))
                  }
                  data-testid={`sab-notify-${o.v ? "yes" : "no"}`}
                  className="accent-primary"
                />
                <span>{o.l}</span>
              </label>
            ))}
          </div>
        </div>
        {form.notify_recipient && (
          <div className="space-y-4">
            <div>
              <FieldLabel>Recipient name</FieldLabel>
              <input
                type="text"
                value={form.recipient_name}
                onChange={set("recipient_name")}
                data-testid="sab-recipient-name"
                className={inputCls}
              />
            </div>
            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <FieldLabel>Recipient email</FieldLabel>
                <input
                  type="email"
                  value={form.recipient_email}
                  onChange={set("recipient_email")}
                  data-testid="sab-recipient-email"
                  className={inputCls}
                />
              </div>
              <div>
                <FieldLabel>Recipient mailing address</FieldLabel>
                <input
                  type="text"
                  value={form.recipient_mailing_address}
                  onChange={set("recipient_mailing_address")}
                  data-testid="sab-recipient-address"
                  placeholder="Street, City, State, ZIP"
                  className={inputCls}
                />
              </div>
            </div>
            <p className="text-[11px] text-ink-500 leading-relaxed">
              BALM will send a simple acknowledgment; the contribution amount
              will not be disclosed.
            </p>
          </div>
        )}
      </fieldset>

      <div>
        <FieldLabel>Optional note</FieldLabel>
        <textarea
          rows={3}
          value={form.note}
          onChange={set("note")}
          data-testid="sab-note"
          className={inputCls + " resize-y"}
          placeholder="Anything you'd like BALM to know about this gift"
        />
      </div>

      <div>
        <FieldLabel>Preferred community, hospice, or organization (optional)</FieldLabel>
        <input
          type="text"
          value={form.preferred_placement}
          onChange={set("preferred_placement")}
          data-testid="sab-preferred-placement"
          placeholder="e.g. Fresno hospice programs, our home church"
          className={inputCls}
        />
        <p className="mt-1.5 text-[11px] text-ink-500">
          BALM will consider this request when making placements, subject to
          program needs and available relationships.
        </p>
      </div>

      {/* Privacy/consent notice */}
      <div className="rounded-xl border border-ink-100 bg-softblue/40 p-4 flex items-start gap-3">
        <ShieldCheck
          size={18}
          weight="duotone"
          className="text-primary mt-0.5 shrink-0"
        />
        <p className="text-xs text-ink-700 leading-relaxed">
          Begin Again Living Ministries will use the information you provide
          only to process your memorial or honor request, communicate with you
          about your sponsorship, and send an acknowledgment when you have
          requested one. Your details are not shared with third parties for
          marketing.
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-3 pt-2">
        <button
          type="submit"
          disabled={submitting}
          data-testid="sab-memorial-submit"
          className="inline-flex items-center gap-2 bg-primary text-white rounded-full px-7 py-3.5 font-semibold hover:bg-primary-dark hover:-translate-y-0.5 hover:shadow-lg transition-all duration-300 disabled:opacity-60"
        >
          <HeartStraight size={18} weight="fill" />
          {submitting ? "Saving…" : "Save details & continue to giving page"}
          <ArrowUpRight size={16} weight="bold" />
        </button>
        <p className="text-[11px] text-ink-500 max-w-xs">
          You'll be forwarded to the secure PayPal giving page to complete
          your contribution.
        </p>
      </div>
    </form>
  );
};

export default MemorialHonorForm;
